// Iter 104 — Extracted from SwipeCards.js verbatim. Bundles Settings + its
// internal helpers (SoundToggleRow, NotificationsCard, HeroModeRow,
// AIStatusCard, WaitlistAdminPanel).
import { useState, useEffect } from "react";
import axios from "axios";
import { hapticTap } from "../../utils/haptics";
import MobilePushToggle from "../MobilePushToggle";
import { soundTap, isSoundMuted, setSoundMuted } from "../../utils/sound";
import { API } from "./_shared";
import { Skel } from "./Skel";
import { clearCache, fastGetAsync } from "../../lib/fastFetch";
import { FloorLanguageControl } from "../FloorLanguageControl";
import { useAuth } from "../../context/AuthContext";
import SettingToggle from "../SettingToggle";

function SoundToggleRow() {
  const [muted, setMuted] = useState(() => isSoundMuted());
  const toggle = (next) => {
    setMuted(!next); // SettingToggle's `checked` means "on" — sound is "on" when NOT muted
    setSoundMuted(!next);
    if (next) soundTap(); // quick confirmation blip when unmuting
  };
  return (
    <div style={{ marginBottom: 16, padding: 16, borderRadius: 14, background: "var(--layer1)", border: "1px solid var(--border)" }}>
      <SettingToggle
        testId="swipe-sound-toggle"
        label="Swipe sounds"
        description="Subtle click on card settle + tap."
        checked={!muted}
        onChange={toggle}
        accent="#60a5fa"
      />
    </div>
  );
}

function NotificationsCard() {
  const [perm, setPerm] = useState(() => typeof Notification !== "undefined" ? Notification.permission : "unsupported");
  const request = async () => {
    if (typeof Notification === "undefined") return;
    try {
      const p = await Notification.requestPermission();
      setPerm(p);
      if (p === "granted") {
        try {
          new Notification("Omega ZI Alerts Enabled", { body: "You'll now receive alerts for Risk Gates, AI exit signals, and critical events.", icon: "/icon-192.png" });
        } catch {}
      }
    } catch {}
  };
  const config = {
    granted: { color: "#4ade80", bg: "rgba(74,222,128,0.08)", label: "Enabled", desc: "You'll receive alerts for Risk Gates + critical events." },
    denied: { color: "#ef4444", bg: "rgba(239,68,68,0.08)", label: "Blocked", desc: "Enable in your browser's site settings to receive alerts." },
    default: { color: "#fbbf24", bg: "rgba(251,191,36,0.08)", label: "Not enabled", desc: "Tap Enable to get Risk Gate alerts + AI exit signals." },
    unsupported: { color: "var(--muted)", bg: "var(--layer1)", label: "Unsupported", desc: "Your browser doesn't support push notifications." },
  }[perm] || { color: "var(--muted)", bg: "var(--layer1)", label: "Unknown", desc: "" };
  return (
    <div style={{ marginBottom: 16, padding: 16, borderRadius: 14, background: "var(--layer1)", border: "1px solid var(--border)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <div style={{ fontSize: 12, color: "var(--text)", fontWeight: 700 }}>Push Notifications</div>
        <span style={{ fontSize: 10, padding: "3px 8px", borderRadius: 6, background: config.bg, color: config.color, fontWeight: 700 }}>{config.label}</span>
      </div>
      <div style={{ fontSize: 11, color: "var(--muted)", lineHeight: 1.5, marginBottom: 10 }}>{config.desc}</div>
      {perm !== "granted" && perm !== "unsupported" && (
        <button onClick={request} data-testid="notifications-enable-btn" style={{ width: "100%", padding: 12, borderRadius: 10, background: "rgba(96,165,250,0.12)", border: "1px solid rgba(96,165,250,0.25)", color: "#60a5fa", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
          Enable Notifications
        </button>
      )}
    </div>
  );
}

// ═══ CARD 6 EXPANDED: Mobile Settings ════════════════════════════════════════
// Hero-mode row — pins the Total Equity hero banner to the top of the home grid.
function HeroModeRow({ heroMode, setHeroMode }) {
  const toggle = (next) => {
    setHeroMode(next);
    hapticTap();
  };
  return (
    <div style={{ marginBottom: 16, padding: 16, borderRadius: 14, background: "var(--layer1)", border: "1px solid var(--border)" }}>
      <SettingToggle
        testId="hero-mode-toggle"
        label="Hero mode"
        description="Pin total equity to the top of the home screen."
        checked={heroMode}
        onChange={toggle}
        accent="#60a5fa"
      />
    </div>
  );
}

// AI Status card — shows live regime, drawdown, and Kelly multiplier.
// Transparency makes the autopilot feel trustworthy.
function AIStatusCard() {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    let stop = false;
    const load = async () => {
      try {
        // Derive values from existing endpoints
        const [pnlR, healthR] = await Promise.all([
          fastGetAsync(`${API}/daily-pnl?period=month`).catch(() => ({ data: {} })),
          axios.get(`${API}/ai/health`).catch(() => ({ data: {} })),
        ]);
        const dd = Math.max(0, -(pnlR.data?.dayPnlPct || 0));
        // Call our own drawdown-kelly endpoint with a standard 0.25 base to display the multiplier
        const dkR = await axios.post(`${API}/ai/drawdown-kelly`, { base_fraction: 0.25, drawdown_pct: dd });
        if (stop) return;
        setStatus({
          ok: healthR.data?.ok,
          version: healthR.data?.version,
          drawdownPct: dd,
          kellyMult: dkR.data?.adjusted_fraction / 0.25,
          capabilities: healthR.data?.capabilities || [],
        });
      } catch {}
    };
    load();
    const iv = setInterval(load, 30000);
    return () => { stop = true; clearInterval(iv); };
  }, []);
  if (!status) return null;
  const kmPct = Math.round((status.kellyMult || 0) * 100);
  return (
    <div style={{ marginBottom: 16, padding: 16, borderRadius: 14, background: "linear-gradient(135deg, rgba(96,165,250,0.08), rgba(167,139,250,0.05))", border: "1px solid rgba(96,165,250,0.22)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#60a5fa", animation: "pulse 2s infinite" }} />
          <span style={{ fontSize: 11, fontWeight: 800, color: "#60a5fa", letterSpacing: 1.5 }}>AI ENHANCEMENTS ACTIVE</span>
        </div>
        <span style={{ fontSize: 9, padding: "2px 8px", borderRadius: 4, background: "rgba(74,222,128,0.15)", color: "#4ade80", fontWeight: 700 }}>
          {status.version || "v1"}
        </span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <div style={{ padding: "8px 10px", borderRadius: 8, background: "var(--layer2)" }} data-testid="ai-status-drawdown">
          <div style={{ fontSize: 9, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 1 }}>Drawdown</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: status.drawdownPct > 5 ? "#f87171" : "var(--text)", marginTop: 2 }}>
            -{status.drawdownPct.toFixed(1)}%
          </div>
        </div>
        <div style={{ padding: "8px 10px", borderRadius: 8, background: "var(--layer2)" }} data-testid="ai-status-kelly">
          <div style={{ fontSize: 9, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 1 }}>Kelly Mult.</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: kmPct < 100 ? "#fbbf24" : "#4ade80", marginTop: 2 }}>
            {kmPct}%
          </div>
        </div>
      </div>
      <div style={{ marginTop: 8, fontSize: 10, color: "var(--muted)", lineHeight: 1.5 }}>
        {kmPct < 100 ? `Recovery mode — position sizes reduced to ${kmPct}% of standard until drawdown recovers.` : "Full Kelly sizing engaged."}
      </div>
      <div style={{ marginTop: 6, display: "flex", flexWrap: "wrap", gap: 4 }}>
        {status.capabilities.slice(0, 6).map(c => (
          <span key={c} style={{ fontSize: 9, padding: "2px 6px", borderRadius: 4, background: "rgba(96,165,250,0.1)", color: "#93c5fd", fontWeight: 600 }}>{c.replace(/-/g, " ")}</span>
        ))}
      </div>
    </div>
  );
}

// ═══ AUTOPILOT QUICK PANEL ═══════════════════════════════════════════════════
const LEVEL_DEFS = [
  { id: "basic", label: "Basic",  color: "#8e8e93", desc: "Long calls/puts only" },
  { id: "pro",   label: "Pro",    color: "#30d158", desc: "+ Spreads, covered calls" },
  { id: "ultra", label: "Ultra",  color: "#60a5fa", desc: "+ Condors, butterflies" },
  { id: "omega", label: "Omega",  color: "#a78bfa", desc: "Full 101-strategy library" },
];

const MODE_LABELS = {
  OFF: "Off", OBSERVE_ONLY: "Observe", ASSISTED: "Assisted",
  GEM_ONLY: "Gem Only", DEFENSE: "Defense", FULL_AUTO: "Full Auto", LIQUIDATE_RISK: "Liquidate",
};

// localStorage key shared with AutopilotExpanded so both panels stay in sync
const AUTOPILOT_MODE_KEY = "omega_mode";
const AUTONOMY_LEVEL_KEY = "omega_autonomy_level";

function _readMode() { try { return localStorage.getItem(AUTOPILOT_MODE_KEY) || null; } catch { return null; } }
function _writeMode(m) { try { localStorage.setItem(AUTOPILOT_MODE_KEY, m); } catch {} }
function _readLevel() { try { return localStorage.getItem(AUTONOMY_LEVEL_KEY) || "omega"; } catch { return "omega"; } }
function _writeLevel(l) { try { localStorage.setItem(AUTONOMY_LEVEL_KEY, l); } catch {} }

function AutopilotQuickPanel() {
  // Seed from localStorage so the panel shows the correct state instantly on every
  // open — no flash from null→skeleton→value, no stale service-worker cache win.
  const [mode, setMode]       = useState(() => _readMode());
  const [level, setLevel]     = useState(() => _readLevel());
  const [saving, setSaving]   = useState("");
  const [flash, setFlash]     = useState("");
  const [saveErr, setSaveErr] = useState(false);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/omega/status`).then(r => {
      if (!alive) return;
      const m = r.data?.autopilotMode || r.data?.mode || "OFF";
      const l = (r.data?.autonomyLevel || "omega").toLowerCase();
      setMode(m);  _writeMode(m);
      setLevel(l); _writeLevel(l);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const isOn = mode ? mode !== "OFF" : false;

  const toggleOnOff = async () => {
    hapticTap();
    const next = isOn ? "OFF" : "FULL_AUTO";
    const prev = mode;
    // Optimistic — write localStorage immediately so re-open shows correct state
    // even if the user navigates away before the network response arrives.
    setMode(next); _writeMode(next);
    setSaving("toggle"); setSaveErr(false);
    try {
      await axios.put(`${API}/settings`, { autopilotMode: next });
      clearCache("/omega/status");  // invalidate fastFetch cache used by AutopilotExpanded
      clearCache("/settings");
      setFlash("toggle");
      setTimeout(() => setFlash(""), 1400);
    } catch {
      // Revert both state and localStorage on failure
      setMode(prev); _writeMode(prev || "OFF");
      setSaveErr(true);
      setTimeout(() => setSaveErr(false), 3000);
    } finally {
      setSaving("");
    }
  };

  const changeLevel = async (lvl) => {
    hapticTap();
    if (saving || lvl === level) return;
    const prev = level;
    setLevel(lvl); _writeLevel(lvl);
    setSaving(`level:${lvl}`);
    try {
      await axios.put(`${API}/omega/autonomy`, { level: lvl });
      clearCache("/omega/status");
      setFlash(`level:${lvl}`);
      setTimeout(() => setFlash(""), 1400);
    } catch {
      setLevel(prev); _writeLevel(prev);
    } finally {
      setSaving("");
    }
  };

  if (mode === null) return (
    <div style={{ marginBottom: 16, padding: 16, borderRadius: 14, background: "var(--layer1)", border: "1px solid var(--border)" }}>
      <div style={{ height: 18, width: 120, borderRadius: 6, background: "var(--border)", marginBottom: 10 }} />
      <div style={{ height: 40, borderRadius: 10, background: "var(--layer2)" }} />
    </div>
  );

  const activeLevel = LEVEL_DEFS.find(l => l.id === level) || LEVEL_DEFS[3];

  return (
    <div
      data-testid="autopilot-quick-panel"
      style={{ marginBottom: 16, padding: 16, borderRadius: 14, background: "var(--layer1)", border: "1px solid var(--border)" }}
    >
      {/* Header row */}
      <div style={{ marginBottom: 14 }}>
        <SettingToggle
          testId="autopilot-onoff-toggle"
          label="Autopilot"
          description={isOn ? `${MODE_LABELS[mode] || mode} · ${activeLevel.label} strategies` : "Scanning paused. Turn on to let Omega scan and trade automatically."}
          checked={isOn}
          onChange={toggleOnOff}
          disabled={saving === "toggle"}
          accent="#30d158"
          size="lg"
          extra={
            <>
              {flash === "toggle" && <span style={{ fontSize: 9, fontWeight: 700, color: "#4ade80" }}>✓ SAVED</span>}
              {saveErr && <span style={{ fontSize: 9, fontWeight: 700, color: "#ff453a" }}>! SAVE FAILED</span>}
            </>
          }
        />
      </div>

      {/* Status badge when ON */}
      {isOn && (
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          padding: "4px 10px", borderRadius: 8, marginBottom: 12,
          background: "rgba(48,209,88,0.10)", border: "1px solid rgba(48,209,88,0.22)",
        }}>
          <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#30d158", animation: "pulse 2s infinite", display: "inline-block" }} />
          <span style={{ fontSize: 10, fontWeight: 700, color: "#30d158" }}>{MODE_LABELS[mode] || mode}</span>
        </div>
      )}

      {/* Strategy level selector */}
      <div style={{ fontSize: 10, color: "var(--muted)", fontWeight: 700, letterSpacing: 1.2, marginBottom: 8 }}>
        STRATEGY LEVEL
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 6 }}>
        {LEVEL_DEFS.map(l => {
          const active = level === l.id;
          const saving_this = saving === `level:${l.id}`;
          const flashed = flash === `level:${l.id}`;
          return (
            <button
              key={l.id}
              data-testid={`autopilot-level-${l.id}`}
              onClick={() => changeLevel(l.id)}
              disabled={!!saving}
              title={l.desc}
              style={{
                padding: "10px 0", borderRadius: 10, cursor: saving ? "default" : "pointer",
                textAlign: "center", border: `1.5px solid ${active ? l.color : "var(--border)"}`,
                background: active ? `${l.color}18` : "var(--layer1)",
                color: active ? l.color : "var(--muted)",
                transition: "all 180ms", opacity: saving && !saving_this ? 0.55 : 1,
              }}
            >
              <div style={{ fontSize: 11, fontWeight: 700 }}>
                {flashed ? "✓" : l.label}
              </div>
            </button>
          );
        })}
      </div>
      <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 8, lineHeight: 1.5 }}>
        {activeLevel.desc}
      </div>
    </div>
  );
}

// ═══ SURVIVAL CAPITAL PANEL ══════════════════════════════════════════════════
const SURVIVAL_AMOUNTS = [
  { label: "Ω $50",    value: 50,   color: "#a78bfa", tag: "omega" },
  { label: "$150",     value: 150,  color: "#fbbf24" },
  { label: "$500",     value: 500,  color: "#4ade80" },
  { label: "$1,000",   value: 1000, color: "#38bdf8" },
  { label: "$1,500",   value: 1500, color: "#60a5fa" },
  { label: "$3,000",   value: 3000, color: "#34d399" },
  { label: "$5,000",   value: 5000, color: "#f97316" },
];

function SurvivalCapitalPanel() {
  const [enabled,  setEnabled]  = useState(false);
  const [capital,  setCapital]  = useState(500);
  const [saving,   setSaving]   = useState(false);
  const [flash,    setFlash]    = useState("");

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/settings`).then(r => {
      if (!alive) return;
      setEnabled(!!r.data?.survivalModeEnabled);
      if (r.data?.survivalCapital) setCapital(r.data.survivalCapital);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const save = async (patch) => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, patch);
      setFlash("saved");
      setTimeout(() => setFlash(""), 1400);
    } catch {}
    setSaving(false);
  };

  const toggleEnabled = () => {
    hapticTap();
    const next = !enabled;
    setEnabled(next);
    save({ survivalModeEnabled: next, survivalCapital: capital });
  };

  const pickAmount = (val) => {
    hapticTap();
    if (saving) return;
    setCapital(val);
    save({ survivalModeEnabled: enabled, survivalCapital: val });
  };

  const active = SURVIVAL_AMOUNTS.find(a => a.value === capital) || SURVIVAL_AMOUNTS[2];

  return (
    <div
      data-testid="survival-capital-panel"
      style={{ marginBottom: 16, padding: 16, borderRadius: 14, background: "var(--layer1)", border: "1px solid var(--border)" }}
    >
      {/* Header + toggle */}
      <div style={{ marginBottom: 12 }}>
        <SettingToggle
          testId="survival-mode-toggle"
          label="Survival Mode Capital"
          description={enabled
            ? `Trading as if account = ${active.label.replace("Ω ", "Ω ")} — Kelly/tier capped`
            : "Omega uses your full account balance"}
          checked={enabled}
          onChange={toggleEnabled}
          disabled={saving}
          accent="#fbbf24"
          extra={flash === "saved" && <span style={{ fontSize: 9, fontWeight: 700, color: "#4ade80" }}>✓ SAVED</span>}
        />
      </div>

      {/* Amount grid — always visible so users can pre-set before enabling */}
      <div style={{ fontSize: 10, color: "var(--muted)", fontWeight: 700, letterSpacing: 1.2, marginBottom: 8 }}>
        CAPITAL LIMIT
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 6 }}>
        {SURVIVAL_AMOUNTS.map(a => {
          const sel = capital === a.value;
          return (
            <button
              key={a.value}
              data-testid={`survival-amount-${a.value}`}
              onClick={() => pickAmount(a.value)}
              disabled={saving}
              title={a.tag === "omega" ? "Ultra-conservative — Omega-level micro sizing ($50)" : `Cap account size at ${a.label}`}
              style={{
                padding: "9px 4px", borderRadius: 10, cursor: saving ? "default" : "pointer",
                textAlign: "center",
                border: `1.5px solid ${sel ? a.color : "var(--border)"}`,
                background: sel ? `${a.color}18` : "var(--layer1)",
                color: sel ? a.color : "var(--muted)",
                fontSize: 11, fontWeight: 700,
                transition: "all 180ms",
                opacity: saving ? 0.55 : 1,
              }}
            >
              {a.label}
            </button>
          );
        })}
      </div>

      {/* Context note */}
      <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 10, lineHeight: 1.5 }}>
        {enabled
          ? `Omega will size all trades as if your capital is ${active.label.replace("Ω ", "")}. Actual account balance is unaffected — only Kelly sizing and tier limits change.`
          : "When enabled, Omega treats your account as a smaller amount for safer position sizing during the Survival tier."}
        {active.tag === "omega" && enabled && (
          <span style={{ color: "#a78bfa", fontWeight: 700 }}> Ω $50 = ultra-micro sizing for the tightest risk control.</span>
        )}
      </div>
    </div>
  );
}

// ═══ WAITLIST ADMIN PANEL — for admin-only access (iter 92) ══════════════════
function WaitlistAdminPanel() {
  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const r = await axios.get(`${API}/waitlist/list?limit=500`);
      setList(r.data);
    } catch (e) {
      setErr(e?.response?.status === 401 ? "Admin access required" : "Unable to load");
    } finally { setLoading(false); }
  };
  useEffect(() => { if (expanded) load(); }, [expanded]);

  const exportCsv = async () => {
    try {
      const r = await axios.get(`${API}/waitlist/export.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `omega-waitlist-${Date.now()}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      setErr("Export failed (admin auth required)");
    }
  };

  return (
    <div data-testid="waitlist-admin-panel" style={{ marginBottom: 12, padding: 16, borderRadius: 14, background: "linear-gradient(135deg, rgba(167,139,250,0.06), rgba(96,165,250,0.03))", border: "1px solid rgba(167,139,250,0.18)" }}>
      <button onClick={() => setExpanded(v => !v)} style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", background: "transparent", border: "none", padding: 0, cursor: "pointer", color: "#a78bfa", fontSize: 12, fontWeight: 700, letterSpacing: 1 }}>
        <span style={{ letterSpacing: 1.5 }}>WAITLIST · ADMIN</span>
        <span style={{ fontSize: 14, color: "var(--muted)" }}>{expanded ? "▾" : "▸"}</span>
      </button>
      {expanded && (
        <div style={{ marginTop: 14 }}>
          {loading && <Skel w="100%" h={60} mb={0} radius={10} />}
          {err && <div style={{ fontSize: 11, color: "#f87171" }}>{err}</div>}
          {list && (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: "var(--text)", lineHeight: 1 }}>{list.total}</div>
                  <div style={{ fontSize: 10, color: "var(--muted)" }}>Total subscribers</div>
                </div>
                <button
                  data-testid="waitlist-csv-export"
                  onClick={exportCsv}
                  style={{ padding: "8px 14px", borderRadius: 10, background: "rgba(96,165,250,0.1)", border: "1px solid rgba(96,165,250,0.28)", color: "#60a5fa", fontSize: 11, fontWeight: 700, cursor: "pointer" }}
                >↓ Export CSV</button>
              </div>
              <div style={{ maxHeight: 240, overflowY: "auto", border: "1px solid var(--layer2)", borderRadius: 10 }}>
                {(list.subscribers || []).map((s, i) => (
                  <div key={i} data-testid={`waitlist-row-${s.position}`} style={{ display: "flex", justifyContent: "space-between", padding: "8px 10px", borderBottom: "0.5px solid var(--layer2)", fontSize: 11 }}>
                    <span style={{ color: "#a78bfa", fontWeight: 700, minWidth: 32 }}>#{s.position}</span>
                    <span style={{ color: "var(--text)", flex: 1, marginLeft: 8, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.email}</span>
                    <span style={{ color: "var(--muted)", fontSize: 10 }}>{s.createdAt ? new Date(s.createdAt).toLocaleDateString() : ""}</span>
                  </div>
                ))}
                {(list.subscribers || []).length === 0 && <div style={{ padding: 16, textAlign: "center", fontSize: 11, color: "var(--muted)" }}>No subscribers yet.</div>}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}


export function MobileSettingsExpanded({ onSwitchToAdvanced, onSetMobileUiMode, currentMode, heroMode, setHeroMode, onOpenLossRecovery, hideSections = [] }) {
  const { logout } = useAuth();
  const [theme, setTheme] = useState(() => localStorage.getItem("zi_theme") || "auto");
  const [mobileUiMode, setMobileUiMode] = useState(currentMode || (() => localStorage.getItem("zi_mobileUiMode") || "swipe3"));
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState("");
  const hidden = new Set(hideSections);

  useEffect(() => {
    axios.get(`${API}/auth/me`).then(r => setProfile(r.data)).catch(() => {});
  }, []);

  const applyTheme = (t) => {
    setTheme(t);
    localStorage.setItem("zi_theme", t);
    // Emit a single window event so App.js (the sole theme owner) syncs.
    // App.js handles both persistence (via /api/user/state) AND writing data-theme,
    // avoiding the race where SwipeCards and App.js both mutated documentElement.
    try { window.dispatchEvent(new CustomEvent("zi-theme-changed", { detail: { theme: t } })); } catch {}
  };

  const applyMobileMode = (m) => {
    setMobileUiMode(m);
    localStorage.setItem("zi_mobileUiMode", m);
    setSaving("mode");
    setTimeout(() => setSaving(""), 700);
    if (onSetMobileUiMode) {
      // App-level handler: navigates + persists + live-switches the active mode
      setTimeout(() => onSetMobileUiMode(m), 250);
    } else if (m === "advanced") {
      // Fallback: switch to desktop view on mobile — close the SwipeCards overlay
      setTimeout(() => onSwitchToAdvanced && onSwitchToAdvanced(), 500);
    }
  };

  const doLogout = async () => {
    if (!window.confirm("Sign out?")) return;
    // Previously just removed a localStorage token — with cookie-based auth
    // that left the server-side session (and cookie) intact, so "logout"
    // silently did nothing. Use the real logout flow instead.
    await logout();
    window.location.reload();
  };

  const themes = [
    { id: "auto", label: "Auto", desc: "Light when market open", icon: "🔄" },
    { id: "dark", label: "Dark", desc: "Always dark", icon: "🌙" },
    { id: "light", label: "Light", desc: "Always light", icon: "☀️" },
  ];

  return (
    <div style={{ padding: "0 16px 32px" }} data-testid="mobile-settings-expanded">
      {!hidden.has("header") && (
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <div style={{ fontSize: 11, color: "var(--muted)", letterSpacing: 2, textTransform: "uppercase" }}>Settings</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: "var(--text)", marginTop: 4 }}>Mobile</div>
        </div>
      )}

      {!hidden.has("uiMode") && (
        <>
          {/* Mobile UI Mode — PRIMARY (user explicitly asked) */}
          <div style={{ marginBottom: 20, padding: 16, borderRadius: 14, background: "var(--layer1)", border: "1px solid var(--border)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <div style={{ fontSize: 12, color: "var(--text)", fontWeight: 700 }}>UI Mode</div>
              {saving === "mode" && <div style={{ fontSize: 9, color: "#4ade80" }}>✓ SAVED</div>}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              {[
                { id: "swipe",  label: "Swipe Cards", desc: "Classic grid + peek",      icon: "📱", color: "#a78bfa" },
                { id: "swipe2", label: "Mobile 2.0",  desc: "Clean · Robinhood UI",     icon: "✦",  color: "#00C805" },
                { id: "swipe3", label: "Trading Floor", desc: "Live floor · glass",     icon: "🛰️", color: "#0A84FF" },
                { id: "glass",  label: "UI Glass", desc: "Glass 2.0 preview",           icon: "◈",  color: "#38bdf8" },
                { id: "advanced", label: "Advanced",  desc: "Back to Desktop view",     icon: "🖥️", color: "#60a5fa" },
              ].map(m => (
                <button key={m.id} onClick={() => applyMobileMode(m.id)} data-testid={`swipe-mobile-ui-${m.id}`} style={{
                  padding: 14, borderRadius: 12, cursor: "pointer", textAlign: "center",
                  background: mobileUiMode === m.id ? `${m.color}20` : "var(--layer1)",
                  border: `1.5px solid ${mobileUiMode === m.id ? m.color : "var(--border)"}`,
                  color: mobileUiMode === m.id ? m.color : "var(--text)",
                }}>
                  <div style={{ fontSize: 22, marginBottom: 4 }}>{m.icon}</div>
                  <div style={{ fontSize: 12, fontWeight: 700 }}>{m.label}</div>
                  <div style={{ fontSize: 9, color: "var(--muted)", marginTop: 2 }}>{m.desc}</div>
                </button>
              ))}
            </div>
            <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 10, lineHeight: 1.5 }}>
              Auto-saved. Your choice persists across sessions. Tap "Advanced" to view the full desktop UI on your mobile device.
            </div>
          </div>
        </>
      )}

      {!hidden.has("floorLanguage") && (
        <div style={{ marginBottom: 16, padding: 16, borderRadius: 14, background: "var(--layer1)", border: "1px solid var(--border)" }} data-testid="mobile-floor-language">
          <FloorLanguageControl />
        </div>
      )}

      {/* Notifications — real VAPID web push (iPhone + Android), with iOS install guidance */}
      {!hidden.has("pushNotifications") && (
        <div style={{ marginBottom: 16 }} data-testid="mobile-push-section">
          <MobilePushToggle c={{ accent: "#60a5fa", text: "var(--text)", sub: "var(--muted)", card: "var(--layer1)", border: "var(--border)", green: "#30d158", red: "#ff453a" }} />
        </div>
      )}

      {/* Theme */}
      {!hidden.has("theme") && (
        <div style={{ marginBottom: 16, padding: 16, borderRadius: 14, background: "var(--layer1)", border: "1px solid var(--border)" }}>
          <div style={{ fontSize: 12, color: "var(--text)", fontWeight: 700 }}>Theme</div>
          <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 2, marginBottom: 10 }}>
            Controls the app's color scheme. Auto switches to light while the market is open and dark after close.
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6 }}>
            {themes.map(t => (
              <button key={t.id} onClick={() => applyTheme(t.id)} data-testid={`swipe-theme-${t.id}`} style={{
                padding: 12, borderRadius: 10, cursor: "pointer", textAlign: "center",
                background: theme === t.id ? "rgba(96,165,250,0.15)" : "var(--layer1)",
                border: `1.5px solid ${theme === t.id ? "#60a5fa" : "var(--border)"}`,
                color: theme === t.id ? "#60a5fa" : "var(--text)",
              }}>
                <div style={{ fontSize: 18, marginBottom: 3 }}>{t.icon}</div>
                <div style={{ fontSize: 11, fontWeight: 700 }}>{t.label}</div>
                <div style={{ fontSize: 8, color: "var(--muted)", marginTop: 1 }}>{t.desc}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Autopilot quick controls — ON/OFF toggle + strategy level selector */}
      <AutopilotQuickPanel />

      {/* Survival mode capital cap — limits Kelly sizing to a chosen dollar amount */}
      <SurvivalCapitalPanel />

      {/* AI enhancements status card — live regime-aware sizing */}
      <AIStatusCard />

      {/* Sound toggle — mutes the subtle swipe/tap click sounds */}
      <SoundToggleRow />

      {/* Hero mode — pin the Total Equity banner to the top of the home grid */}
      <HeroModeRow heroMode={heroMode} setHeroMode={setHeroMode} />

      {/* Iter 100 — Loss Recovery Planner entrypoint */}
      <button
        data-testid="settings-open-recovery"
        onClick={() => onOpenLossRecovery && onOpenLossRecovery()}
        style={{
          width: "100%", marginBottom: 12, padding: 16, borderRadius: 14,
          background: "linear-gradient(135deg, rgba(167,139,250,0.16), rgba(167,139,250,0.04))",
          border: "1px solid rgba(167,139,250,0.32)",
          color: "var(--text)", textAlign: "left", cursor: "pointer",
          WebkitTapHighlightColor: "transparent",
        }}
      >
        <div style={{ fontSize: 11, fontWeight: 800, color: "#a78bfa", letterSpacing: 1.5, marginBottom: 4 }}>RECOVERY PLANNER</div>
        <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text)", marginBottom: 3 }}>
          Recover from a loss
        </div>
        <div style={{ fontSize: 11, color: "var(--muted)", lineHeight: 1.4 }}>
          Kelly-sized per-trade risk · Monte-Carlo recovery probability · step-by-step plan
        </div>
      </button>

      {/* Account / Sign out */}
      <div style={{ marginBottom: 12, padding: 16, borderRadius: 14, background: "var(--layer1)", border: "1px solid var(--border)" }}>
        <div style={{ fontSize: 12, color: "var(--text)", fontWeight: 700, marginBottom: 10 }}>Account</div>
        {profile && (
          <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 10 }}>
            <div>Signed in as <strong style={{ color: "var(--text)" }}>{profile.name || profile.username}</strong></div>
            {profile.email && <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}>{profile.email}</div>}
          </div>
        )}
        <button onClick={doLogout} data-testid="mobile-logout" style={{ width: "100%", padding: 12, borderRadius: 10, background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.22)", color: "#f87171", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
          Sign Out
        </button>
      </div>

      {/* Admin: Waitlist Dashboard (iter 92) */}
      {profile && (profile.role === "admin" || profile.username) && <WaitlistAdminPanel />}

      {/* App Info */}
      <div style={{ textAlign: "center", padding: "14px 0" }}>
        <div style={{ fontSize: 10, color: "var(--muted)" }}>Omega ZI AI · v6.0</div>
        <div style={{ fontSize: 9, color: "var(--muted)", marginTop: 2 }}>Ultra-intelligent options trading</div>
      </div>
    </div>
  );
}
